import "./App.css";
import { Toaster } from "react-hot-toast";
import { Layout } from "./Layout/Layout";
import { useTheme } from "next-themes";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import socketService from "./Utils/socketService";
import { notifyError, notifySuccess } from "./Utils/Helper";
import { useInitializeWhitelabel } from "./Utils/getWhitelabelDetail";
import { getDashboardData, getUserdata } from "./Redux/Action";
import { applyDomainBranding } from "./Utils/dynamicBranding";
import { WhitelabelProvider } from "./Components/custom/WhitelabelProvider";
import { ThemeProvider } from "./Context/ThemeContext";

function App() {
  // Apply domain branding as early as possible
  applyDomainBranding();
  
  const { setTheme } = useTheme();

  // Check for successful Stripe payment and refresh if needed
  // Only run this in the main window, not in popup windows
  useEffect(() => {
    // Skip this check if we're in a popup window (like the payment result page)
    if (window.opener) {
      return;
    }
    
    const checkStripePaymentSuccess = () => {
      if (localStorage.getItem('stripePaymentSuccess') === 'true') {
        // Remove the flag immediately to prevent multiple refreshes
        localStorage.removeItem('stripePaymentSuccess');
        // Refresh the page to show updated balance
        window.location.reload();
      }
    };

    // Check on app load
    checkStripePaymentSuccess();

    // Also check when the tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkStripePaymentSuccess();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state?.Login?.isAuthenticated);
  const userData = useSelector((state) => state?.Login?.userData);
  const { loading: whitelabelLoading } = useInitializeWhitelabel();
  const fetchUserData = () => {
    if (isAuthenticated) {
      dispatch(getUserdata());
    }
  };

  const handleBalanceUpdate = (data) => {
    const { status } = data;
    if (status === "success") {
      notifySuccess("Transaction Success.");
    } else {
      notifyError("Transaction Failed.")
    }
    fetchUserData();
  };

  const fetchDashboardData = () => {
    dispatch(getDashboardData({
      userId: userData?._id,
      accountno: userData?.accountno
    }))
  }
 


  useEffect(() => {
    if (userData._id && userData.accountno) {
      fetchDashboardData();

    }
  }, [userData._id,userData.accountno]);

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleNotification = (data) => {
    fetchUserData();
  };

  const handleCryptoUpdate = (data) => {
    fetchUserData();
    if (data?.status === "success") {
      notifySuccess(data?.message);
    } else {
      notifyError(data?.message);
    }
  };

  useEffect(() => {
    socketService.on(`kyc_new_status_${userData?._id}`, handleNotification);
    socketService.on(`crypto_newStatus_${userData?._id}`, handleCryptoUpdate);
    socketService.on(`balanceUpdated_${userData?._id}`, handleBalanceUpdate);

    return () => {
      socketService.off(`kyc_new_status_${userData?._id}`, handleNotification);
      socketService.off(
        `crypto_newStatus_${userData?._id}`,
        handleCryptoUpdate
      );
      socketService.off(`balanceUpdated_${userData?._id}`, handleBalanceUpdate);
    };
  }, []);

  useEffect(() => {
    const logAllSocketEvents = (event, ...args) => {
      console.log(`🛰️ Socket Event Received: ${event}`, ...args);
    };

    socketService.onAny(logAllSocketEvents);
    return () => {
      socketService.offAny(logAllSocketEvents);
    };
  }, []);

  return (
    <ThemeProvider>
      <WhitelabelProvider whitelabelLoading={whitelabelLoading}>
        <Layout />
        <Toaster />
      </WhitelabelProvider>
    </ThemeProvider>
  );
}

export default App;